// ==============================
// Button
// ==============================

import { darken, fade, lighten } from '../colorUtils'
import themeOLD from '../theme'
import { colors } from '@lmc/common/constants'
import { theme } from '@lmc/ui-kit'

// Common Styles
// ----------------

export const common = {
  // Base Button
  // ----------------
  base: {
    appearance: 'none',
    background: 'none',
    borderWidth: 0, // theme.button.borderWidth,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: themeOLD.button.borderRadius,
    cursor: 'pointer',
    display: 'inline-block',
    fontWeight: themeOLD.button.font.weight,
    height: themeOLD.component.height,
    lineHeight: themeOLD.component.lineHeight,
    marginBottom: 0,
    padding: `0 ${themeOLD.button.paddingHorizontal}`,
    outline: 0,
    textAlign: 'center',
    touchAction: 'manipulation',
    userSelect: 'none',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',

    ':hover': {
      color: themeOLD.button.default.textColor,
      textDecoration: 'none',
    },
    ':focus': {
      color: themeOLD.button.default.textColor,
      textDecoration: 'none',
    },
  },
  // Block Display
  // ----------------
  block: {
    display: 'block',
    width: '100%',
  },
  // Disabled
  // ----------------
  disabled: {
    opacity: 0.4,
    pointerEvents: 'none',
  },
  // Sizes
  // ----------------
  large: {
    fontSize: themeOLD.font.size.large,
  },
  default: {
    fontSize: themeOLD.font.size.default,
  },
  small: {
    fontSize: themeOLD.font.size.small,
  },
  xsmall: {
    fontSize: themeOLD.font.size.xsmall,
    lineHeight: '1.9',
    paddingLeft: '.66em',
    paddingRight: '.66em',
  },
}

// Fill Variant
// ----------------
function buttonFillVariant(textColor, bgColor) {
  const hoverStyles = {
    background: lighten(bgColor, 10),
    // ...gradientVertical(lighten(bgColor, 10), darken(bgColor, 5)),
    borderColor: `${darken(bgColor, 5)} ${darken(bgColor, 10)} ${darken(
      bgColor,
      15
    )}`,
    boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
    color: textColor,
    outline: 'none',
  }
  const focusStyles = {
    background: bgColor,
    // ...gradientVertical(lighten(bgColor, 10), darken(bgColor, 5)),
    borderColor: `${darken(bgColor, 5)} ${darken(bgColor, 10)} ${darken(
      bgColor,
      15
    )}`,
    boxShadow: `0 0 0 3px ${fade(bgColor, 25)}`,
    color: textColor,
    outline: 'none',
  }
  const activeStyles = {
    backgroundColor: darken(bgColor, 10),
    backgroundImage: 'none',
    borderColor: `${darken(bgColor, 25)} ${darken(bgColor, 15)} ${darken(
      bgColor,
      10
    )}`,
    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
  }
  return {
    base: {
      // ...gradientVertical(lighten(bgColor, 5), darken(bgColor, 10), bgColor),
      background: bgColor,
      borderColor: `${darken(bgColor, 10)} ${darken(bgColor, 20)} ${darken(
        bgColor,
        25
      )}`,
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      color: textColor,
      fontWeight: 600,
      // 'textShadow': '0 -1px 0 rgba(0, 0, 0, 0.25)',

      ':hover': hoverStyles,
      ':focus': focusStyles,
      ':active': activeStyles,
    },
    active: activeStyles,
  }
}
// TODO: This is pretty hacky, needs to be consolidated with the Variant() method
// above (needs more theme variables to be implemented though)
function buttonFillDefault() {
  const borderColor = themeOLD.input.border.color.default
  const hoverStyles = {
    background: '#f0f0f0',
    // ...gradientVertical('#fff', '#eee'),
    borderColor: `${darken(borderColor, 5)} ${darken(borderColor, 5)} ${darken(
      borderColor,
      10
    )}`,
    color: themeOLD.color.text,
  }
  const focusStyles = {
    borderColor: themeOLD.color.create,
    boxShadow: `0 0 0 3px ${fade(themeOLD.color.create, 10)}`,
    color: themeOLD.color.text,
    outline: 'none',
  }
  const activeStyles = {
    background: '#e6e6e6',
    borderColor: darken(borderColor, 10),
    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
    color: themeOLD.color.text,
  }
  return {
    base: {
      // ...gradientVertical('#fafafa', '#eaeaea'),
      background: colors.grey,
      borderColor: `${borderColor} ${darken(borderColor, 6)} ${darken(
        borderColor,
        12
      )}`,
      color: themeOLD.color.text,
      textShadow: '0 1px 0 white',

      ':hover': hoverStyles,
      ':focus': focusStyles,
      ':active': activeStyles,
    },

    // gross hack
    active: {
      ...activeStyles,

      ':hover': activeStyles,
      ':focus': {
        ...activeStyles,
        ...focusStyles,
        boxShadow: `0 0 0 3px ${fade(
          themeOLD.color.create,
          10
        )}, inset 0 1px 2px rgba(0, 0, 0, 0.1)`,
      },
      ':active': activeStyles,
    },
  }
}

export const fill = (color) => {
  switch (color) {
    case 'default':
      return buttonFillDefault()
    case 'cancel':
    case 'delete':
      return buttonFillVariant('white', themeOLD.button.danger.bgColor)
    default:
      return buttonFillVariant('white', themeOLD.button[color].bgColor)
  }
}

// Hollow Variant
// ----------------
function buttonHollowVariant(textColor, borderColor) {
  const focusAndHoverStyles = {
    backgroundImage: 'none',
    backgroundColor: fade(borderColor, 15),
    borderColor: darken(borderColor, 15),
    boxShadow: 'none',
    color: textColor,
    outline: 'none',
  }
  const focusOnlyStyles = {
    boxShadow: `0 0 0 3px ${fade(borderColor, 10)}`,
  }
  const activeStyles = {
    backgroundColor: fade(borderColor, 35),
    borderColor: darken(borderColor, 25),
    boxShadow: 'none',
  }

  return {
    base: {
      background: 'none',
      borderColor: borderColor,
      color: textColor,

      ':hover': focusAndHoverStyles,
      ':focus ': Object.assign({}, focusAndHoverStyles, focusOnlyStyles),
      ':active': activeStyles,
    },
    active: activeStyles,
  }
}
export const hollow = (color) => {
  // TODO: better handling of cancel and delete colors
  if (color === 'cancel' || color === 'delete') color = 'danger'

  return buttonHollowVariant(
    themeOLD.button[color].bgColor,
    themeOLD.button[color].borderColor
  )
}

// Link Variant
// ----------------
function buttonLinkVariant(textColor, hoverColor) {
  const hoverStyles = {
    color: hoverColor,
    textDecoration: 'underline',
  }
  return {
    base: {
      background: 'none',
      border: 0,
      boxShadow: 'none',
      color: textColor,
      fontWeight: 'normal',
      outline: 'none',

      ':hover': hoverStyles,
      ':focus': hoverStyles,
      ':active': hoverStyles,
    },
    active: hoverStyles,
  }
}
function buttonLinkDelete() {
  const styles = buttonLinkVariant(themeOLD.color.gray40, themeOLD.color.danger)
  const hoverStyles = {
    // ...gradientVertical(lighten(theme.color.danger, 10), darken(theme.color.danger, 10)),
    backgroundColor: themeOLD.color.danger,
    borderColor: `${darken(themeOLD.color.danger, 4)} ${darken(
      themeOLD.color.danger,
      8
    )} ${darken(themeOLD.color.danger, 12)}`,
    boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
    color: 'white',
    textDecoration: 'none',
  }
  const activeStyles = {
    backgroundColor: darken(themeOLD.color.danger, 4),
    backgroundImage: 'none',
    borderColor: `${darken(themeOLD.color.danger, 12)} ${darken(
      themeOLD.color.danger,
      8
    )} ${darken(themeOLD.color.danger, 8)}`,
    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
    color: 'white',
  }
  return {
    base: {
      ...styles.base,
      ':hover': hoverStyles,
      ':focus': hoverStyles,
      ':active': activeStyles,
    },
    active: activeStyles,
  }
}

export const link = (color) => {
  switch (color) {
    case 'default':
      return buttonLinkVariant(
        theme.color.status.primary,
        theme.color.status.primary
      )
    case 'cancel':
      return buttonLinkVariant(themeOLD.color.gray40, themeOLD.color.danger)
    case 'delete':
      return buttonLinkDelete()
    default:
      return buttonLinkVariant(themeOLD.color[color], themeOLD.color[color])
  }
}

const styles = {
  common,
  link,
  fill,
  hollow,
}

export default styles
